import React, { FunctionComponent, MouseEventHandler } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { ALIGNMENT } from 'ui/menu/constants';
import { Overflow } from 'ui/icons/navigation/Overflow';
import { colors } from 'ui/theme/colors';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { NoOpComponent } from 'utils/reactHelpers';
import { ACTIONS, EDIT_GOAL, DELETE_GOAL } from 'modules/predictiveGoals/constants';
import {
  GoalDelete,
  GoalEdit,
  MenuOptionDisabled,
  MenuOptionEnabled,
  PaddedBaseMenu,
} from './Styled';
import { GoalMenuOption } from '../types';

export type CreatedPageActions = {
  canManageGoals?: boolean;
  customOptions?: Array<GoalMenuOption>;
  isDeleteDisabled: boolean;
  isEditDisabled: boolean;
  onDelete: MouseEventHandler;
  onEdit: MouseEventHandler;
};

export type MenuOptionConfig = {
  CreateMenuComponent: FunctionComponent<React.PropsWithChildren<CreateMenuComponentProps>>;
  id: string;
  isDivider?: boolean;
};

export type CreateMenuComponentProps = {
  config: CreatedPageActions;
};

export const MENU_PRG_ACTIONS_ID = 'prg-actions-id';

export function createMenuOption(
  menuConfig: CreatedPageActions,
  menuItem: MenuOptionConfig,
): GoalMenuOption {
  if (menuItem.isDivider) {
    return {
      isDivider: true,
      id: menuItem.id,
      children: null,
    };
  }
  return {
    id: menuItem.id,
    children: <menuItem.CreateMenuComponent config={menuConfig} />,
  };
}

export function CreateDeleteMenuComponent(props: CreateMenuComponentProps) {
  const isDisabled = props.config.isDeleteDisabled;
  const onDeleteHandler = isDisabled ? () => null : props.config.onDelete;
  const DeleteComponent = isDisabled ? MenuOptionDisabled : MenuOptionEnabled;
  return (
    <DeleteComponent data-destructive onClick={onDeleteHandler} role="button" tabIndex={0}>
      <GoalDelete color={isDisabled ? colors.grey25 : colors.iconDestructive} />
      {DELETE_GOAL}
    </DeleteComponent>
  );
}

export function CreateEditMenuComponent(props: CreateMenuComponentProps) {
  const isDisabled = props.config.isEditDisabled;
  const onEditHandler = isDisabled ? () => null : props.config.onEdit;
  const EditComponent = isDisabled ? MenuOptionDisabled : MenuOptionEnabled;
  return (
    <EditComponent onClick={onEditHandler} role="button" tabIndex={0}>
      <GoalEdit color={isDisabled ? colors.grey25 : colors.grey60} />
      {EDIT_GOAL}
    </EditComponent>
  );
}

export const menuOptions = [
  {
    CreateMenuComponent: CreateEditMenuComponent,
    id: 'editGoal',
  },
  {
    CreateMenuComponent: NoOpComponent,
    isDivider: true,
    id: 'actionDivider1',
  },
  {
    CreateMenuComponent: CreateDeleteMenuComponent,
    id: 'deleteGoal',
  },
];
export function createMenuOptions(menuConfig: CreatedPageActions): Array<GoalMenuOption> {
  return menuOptions
    .map((menuOption) => {
      if (!isEmpty(menuOption)) {
        return createMenuOption(menuConfig, menuOption as MenuOptionConfig);
      }
      return null;
    })
    .filter(Boolean) as Array<GoalMenuOption>;
}

export function createPageActions(config: CreatedPageActions) {
  return function PageActions() {
    const menuAnchor = (
      <Tooltip tooltipContent={ACTIONS}>
        <IconButton variant={BUTTON_VARIANTS.SECONDARY} isDisabled={!config.canManageGoals}>
          <Overflow />
        </IconButton>
      </Tooltip>
    );
    if (!config.canManageGoals) {
      return menuAnchor;
    }

    const pageMenuOptions = isEmpty(config.customOptions)
      ? createMenuOptions(config)
      : (config.customOptions as Array<GoalMenuOption>);

    return (
      <PaddedBaseMenu
        id={MENU_PRG_ACTIONS_ID}
        horizontalAlignment={ALIGNMENT.RIGHT}
        options={pageMenuOptions}
      >
        {menuAnchor}
      </PaddedBaseMenu>
    );
  };
}
